import type { WidgetKind } from "@playbookai/types";
import type { WidgetDef } from "./types.js";

/**
 * Central widget registry. Populated eagerly at module load — no runtime
 * plugin registration. Cross-module widgets self-register from this package;
 * module-specific widgets register from their feature package via
 * `registerWidget()` at import time.
 *
 * If a kind is missing at runtime, callers skip it with a diagnostic so an
 * unknown widget doesn't crash the page. The CHECK constraint on
 * `collab.documents.doc_type` and the BE projection guard make this a
 * defense-in-depth fallback.
 *
 * **A `Map`, deliberately — do not "simplify" this back to an object literal.**
 * `kind` reaches `getWidget` from untrusted sources (a Yjs doc any client can
 * write, a server placement row), and a plain `{}` resolves INHERITED keys: a
 * doc containing `__proto__`, `constructor`, `toString`, `valueOf`,
 * `hasOwnProperty` or `isPrototypeOf` returned a truthy non-def, sailed past
 * every `if (!def)` guard, and crashed the render on the first missing field.
 * A Map has no prototype chain, so an unregistered name is `undefined` whatever
 * it is called. This protects all `getWidget` callers at once — the canvas, the
 * dashboard, and the Hoshin/VSM/SOLVE feature packages.
 */
const REGISTRY = new Map<WidgetKind, WidgetDef<unknown>>();

export function registerWidget<TParams, TView = TParams>(
  def: WidgetDef<TParams, TView>,
): void {
  if (REGISTRY.has(def.kind)) {
    // Re-registration is a programming error (same kind in two registry calls).
    // Keep HMR resilient, but do not depend on Node globals in browser code.
    console.warn(
      `@playbookai/ui: widget '${def.kind}' is already registered; replacing it`,
    );
  }
  REGISTRY.set(def.kind, def as unknown as WidgetDef<unknown>);
}

export function getWidget(kind: WidgetKind): WidgetDef<unknown> | undefined {
  return REGISTRY.get(kind);
}

export function allWidgets(): ReadonlyArray<WidgetDef<unknown>> {
  return [...REGISTRY.values()];
}

/** Test-only: clear the registry between specs. */
export function __resetWidgetRegistry(): void {
  REGISTRY.clear();
}
